/**
 * CMS Data Layer
 * Čita iz Supabase ako je konfigurisano, inače vraća mock podatke.
 * Admin panel piše isključivo u Supabase.
 */
import { supabase, isSupabaseConfigured } from './supabase'
import { products as mockProducts, productCategories as mockProductCategories } from '../mocks/products'
import { blogPosts as mockBlogPosts, blogCategories as mockBlogCategories } from '../mocks/blog'
import { faqs as mockFaqs } from '../mocks/faqs'
import { testimonials as mockTestimonials } from '../mocks/testimonials'
import { industries as mockIndustries } from '../mocks/industries'
import { projects as mockProjects } from '../mocks/projects'
import {
  heroContent,
  aboutContent,
  categoriesContent,
  solutionsContent,
  ctaContent,
  footerContent,
} from '../mocks/homepage'
import type { Product } from '../mocks/products'

// ─── Types ────────────────────────────────────────────────

export interface CmsSettings {
  id: string
  site_name: string
  site_tagline: string
  site_description: string
  canonical_url: string
  meta_description: string
  og_title: string
  og_description: string
  og_image: string
  google_analytics_id: string
  google_tag_manager_id: string
  facebook_pixel_id: string
  tiktok_pixel_id: string
  custom_head_scripts: string
  custom_body_scripts: string
  phone: string
  email: string
  address: string
}

export const defaultSettings: CmsSettings = {
  id: 'global',
  site_name: 'SafeGuard Systems',
  site_tagline: 'Vaša bezbednost je naša misija.',
  site_description: 'SafeGuard Systems – profesionalna kontrola pristupa, video nadzor, IP kamere, video interfoni i alarmni sistemi.',
  canonical_url: 'https://safeguard-systems.rs/',
  meta_description: '',
  og_title: '',
  og_description: '',
  og_image: '',
  google_analytics_id: '',
  google_tag_manager_id: '',
  facebook_pixel_id: '',
  tiktok_pixel_id: '',
  custom_head_scripts: '',
  custom_body_scripts: '',
  phone: '+381-11-000-0000',
  email: 'info@safeguard-systems.rs',
  address: 'Srbija',
}

// ─── Settings ─────────────────────────────────────────────

export async function getSettings(): Promise<CmsSettings> {
  if (!isSupabaseConfigured) return defaultSettings
  const { data } = await supabase.from('cms_settings').select('*').eq('id', 'global').single()
  return data ?? defaultSettings
}

export async function saveSettings(settings: Partial<CmsSettings>): Promise<void> {
  await supabase
    .from('cms_settings')
    .upsert({ ...settings, id: 'global', updated_at: new Date().toISOString() })
}

// ─── Products ─────────────────────────────────────────────

export async function getProducts(): Promise<Product[]> {
  if (!isSupabaseConfigured) return mockProducts
  const { data, error } = await supabase
    .from('cms_products')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  if (error || !data?.length) return mockProducts
  return data.map(dbProductToProduct)
}

export async function getProductById(id: string): Promise<Product | undefined> {
  if (!isSupabaseConfigured) return mockProducts.find(p => p.id === id)
  const { data } = await supabase.from('cms_products').select('*').eq('id', id).single()
  if (!data) return mockProducts.find(p => p.id === id)
  return dbProductToProduct(data)
}

export async function saveProduct(product: Product): Promise<void> {
  await supabase.from('cms_products').upsert({
    ...productToDb(product),
    updated_at: new Date().toISOString(),
  })
}

export async function deleteProduct(id: string): Promise<void> {
  await supabase.from('cms_products').delete().eq('id', id)
}

function dbProductToProduct(row: any): Product {
  return {
    id: row.id,
    name: row.name,
    category: row.category,
    categorySlug: row.category_slug,
    shortDesc: row.short_desc,
    longDesc: row.long_desc,
    price: row.price,
    image: row.image,
    gallery: row.gallery ?? [],
    specs: row.specs ?? [],
    features: row.features ?? [],
    benefits: row.benefits ?? [],
    isFeatured: row.is_featured ?? false,
  }
}

function productToDb(p: Product) {
  return {
    id: p.id,
    name: p.name,
    category: p.category,
    category_slug: p.categorySlug,
    short_desc: p.shortDesc,
    long_desc: p.longDesc,
    price: p.price,
    image: p.image,
    gallery: p.gallery,
    specs: p.specs,
    features: p.features,
    benefits: p.benefits,
    is_featured: p.isFeatured,
    is_active: true,
  }
}

// ─── Blog ─────────────────────────────────────────────────

export async function getBlogPosts() {
  if (!isSupabaseConfigured) return mockBlogPosts
  const { data, error } = await supabase
    .from('cms_blog_posts')
    .select('*')
    .eq('is_published', true)
    .order('created_at', { ascending: false })
  if (error || !data?.length) return mockBlogPosts
  return data.map(dbToPost)
}

export async function getBlogPostBySlug(slug: string) {
  if (!isSupabaseConfigured) return mockBlogPosts.find(p => p.slug === slug)
  const { data } = await supabase.from('cms_blog_posts').select('*').eq('slug', slug).single()
  if (!data) return mockBlogPosts.find(p => p.slug === slug)
  return dbToPost(data)
}

export async function saveBlogPost(post: any): Promise<void> {
  await supabase.from('cms_blog_posts').upsert({
    ...post,
    updated_at: new Date().toISOString(),
  })
}

export async function deleteBlogPost(id: string): Promise<void> {
  await supabase.from('cms_blog_posts').delete().eq('id', id)
}

function dbToPost(row: any) {
  return {
    id: row.id,
    title: row.title,
    slug: row.slug,
    excerpt: row.excerpt,
    content: row.content,
    category: row.category,
    categorySlug: row.category_slug,
    image: row.image,
    date: row.date,
    author: row.author,
    authorRole: row.author_role,
    readTime: row.read_time,
    is_published: row.is_published,
  }
}

// ─── FAQs ─────────────────────────────────────────────────

export async function getFaqs() {
  if (!isSupabaseConfigured) return mockFaqs
  const { data, error } = await supabase
    .from('cms_faqs')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  if (error || !data?.length) return mockFaqs
  return data
}

export async function saveFaq(faq: { id?: number; question: string; answer: string; sort_order?: number }): Promise<void> {
  if (faq.id) {
    await supabase.from('cms_faqs').update({ question: faq.question, answer: faq.answer, sort_order: faq.sort_order }).eq('id', faq.id)
  } else {
    await supabase.from('cms_faqs').insert({ question: faq.question, answer: faq.answer, sort_order: faq.sort_order ?? 0 })
  }
}

export async function deleteFaq(id: number): Promise<void> {
  await supabase.from('cms_faqs').delete().eq('id', id)
}

// ─── Testimonials ──────────────────────────────────────────

export async function getTestimonials() {
  if (!isSupabaseConfigured) return mockTestimonials
  const { data, error } = await supabase
    .from('cms_testimonials')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  if (error || !data?.length) return mockTestimonials
  return data
}

export async function saveTestimonial(t: any): Promise<void> {
  if (t.id) {
    await supabase.from('cms_testimonials').update(t).eq('id', t.id)
  } else {
    const { id: _id, ...rest } = t
    await supabase.from('cms_testimonials').insert(rest)
  }
}

export async function deleteTestimonial(id: number): Promise<void> {
  await supabase.from('cms_testimonials').delete().eq('id', id)
}

// ─── Industries ───────────────────────────────────────────

export async function getIndustries() {
  if (!isSupabaseConfigured) return mockIndustries
  const { data, error } = await supabase
    .from('cms_industries')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  if (error || !data?.length) return mockIndustries
  return data
}

export async function saveIndustry(item: any): Promise<void> {
  if (item.id) {
    await supabase.from('cms_industries').update(item).eq('id', item.id)
  } else {
    const { id: _id, ...rest } = item
    await supabase.from('cms_industries').insert(rest)
  }
}

export async function deleteIndustry(id: number): Promise<void> {
  await supabase.from('cms_industries').delete().eq('id', id)
}

// ─── Projects ─────────────────────────────────────────────

export async function getProjects() {
  if (!isSupabaseConfigured) return mockProjects
  const { data, error } = await supabase
    .from('cms_projects')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  if (error || !data?.length) return mockProjects
  return data
}

export async function saveProject(item: any): Promise<void> {
  if (item.id) {
    await supabase.from('cms_projects').update(item).eq('id', item.id)
  } else {
    const { id: _id, ...rest } = item
    await supabase.from('cms_projects').insert(rest)
  }
}

export async function deleteProject(id: number): Promise<void> {
  await supabase.from('cms_projects').delete().eq('id', id)
}

// ─── Homepage sections ────────────────────────────────────

const defaultHomepageSections: Record<string, any> = {
  hero: heroContent,
  about: aboutContent,
  categories: categoriesContent,
  solutions: solutionsContent,
  cta: ctaContent,
  footer: footerContent,
}

export async function getHomepageSection(id: string): Promise<any> {
  if (!isSupabaseConfigured) return defaultHomepageSections[id] ?? {}
  const { data } = await supabase.from('cms_homepage').select('content').eq('id', id).single()
  return data?.content ?? defaultHomepageSections[id] ?? {}
}

export async function saveHomepageSection(id: string, content: any): Promise<void> {
  await supabase.from('cms_homepage').upsert({ id, content, updated_at: new Date().toISOString() })
}

// Re-export mock categories (used in filter UIs)
export { mockProductCategories as productCategories, mockBlogCategories as blogCategories }
